<template>
  <div class="container-fluid" id="expenses">
    <div class="row wrapper">
      <div class="col-12 col-md-4">
        <div class="header">
          <div id="steps" class="steps" tabindex="0" role="document">
            <span>Step 4</span> of 5
          </div>
          <h1 tabindex="0">Future expenditure</h1>
          <div class="hero-image" alt="Future expenses"></div>
        </div>
      </div>
      <div class="col-12 col-md-8">
        <div class="main">
          <ValidationObserver v-slot="{ valid }" ref="form">
            <form @submit.prevent="onSubmit">
              <h2>Your future income needs</h2>
              <div v-if="data.stepData.familyType != Enum.familyType.SINGLE">
                <div class="row no-gutters mt-4">
                  <div class="col-auto">
                    <h3 tabindex="0">Living expenses</h3>
                  </div>
                  <div class="col-auto">
                    <b-button id="tooltip-expenses" variant="tooltip" aria-label="tooltip expenses" v-if="data.stepData.familyType !== Enum.familyType.SINGLE"><i class="icon-help"></i></b-button>
                    <b-tooltip target="tooltip-expenses" placement="top">
                      <span v-if="data.stepData.familyType === Enum.familyType.COUPLE || data.stepData.familyType === Enum.familyType.COUPLE_DEPENDANT" v-html="pageContent.pages.expenses.livingExpensesCoupleTooltipText"></span>
                      <span v-if="data.stepData.familyType === Enum.familyType.SINGLE_DEPENDANT" v-html="pageContent.pages.expenses.livingExpensesSingleTooltipText">
                      </span>
                    </b-tooltip>
                  </div>
                </div>

                <div v-if="!detailedExpenses">
                  <p v-if="pageContent.pages.expenses.livingExpenses">{{pageContent.pages.expenses.livingExpenses}}</p>
                  <div class="form-group">
                    <ValidationProvider mode="lazy" name="expensesAmountPerMonth" :rules="'required:'+pageContent.pages.expenses.livingExpensesError+'|between:'+config.fields.expensesAmountPerMonth.min+','+config.fields.expensesAmountPerMonth.max+','+pageContent.pages.expenses.livingExpensesError" v-slot="{ errors }">
                      <label for="expensesAmountPerMonth" tabindex="0">Amount per month (excluding debt repayments, rent &amp; mortgage)</label>
                        <cleave
                          data-analytics-capture-value="true"
                          :options="formatOptions.moneys"
                          class="form-control"
                          id="expensesAmountPerMonth"
                          name="expensesAmountPerMonth"
                          v-model.number="data.stepData.expensesAmountPerMonth"
                          v-on:blur="triggerHeightCheck"
                          type="text"
                          tabindex="0">
                        </cleave>
                        <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                      </ValidationProvider>
                  </div>
                </div>

                <label class="checkbox-wrapper mt-4 mb-4">
                  <input type="checkbox" id="detailedExpenses" name="detailedExpenses" @change="clickDetailedExpenses" v-model="detailedExpenses" tabindex="0" />
                  <span class="checkmark"></span>
                  Enter a breakdown of my living expenses
                </label>

                <div class="detailed-expenses card"  v-if="detailedExpenses" >
                  <div class="card-body max">
                    <div class="form-row ">
                      <div class="col">
                        <label tabindex="0">Expenses</label><br/>
                      </div>
                      <div class="col">
                        <label tabindex="0">Cost per month</label><br/>
                      </div>
                    </div>

                    <!-- expenses -->
                    <div class="form-row repeat-input mb-2" v-for="(expense, ind) in expenses" :key="ind">
                      <div class="col">
                        <select class="form-control" aria-label="Default select example" v-model="expense.Name" tabindex="0">
                          <option selected disabled value="">Select expense</option>
                          <option v-for="(confExp, ind) in config.livingExpenses" :key="ind" :value="confExp.value">{{confExp.label}}</option>
                        </select>
                      </div>
                      <div class="col">
                        <ValidationProvider mode="lazy" :name="'expense_'+ind" :rules="'required:'+pageContent.pages.expenses.expenseRangeErrorMessage+'|between:'+config.fields.expenses.min+','+config.fields.expenses.max+','+pageContent.pages.expenses.expenseRangeErrorMessage" v-slot="{ errors }">
                          <cleave
                            data-analytics-capture-value="true"
                            :class="{'is-muted': !expense.Name}"
                            :options="formatOptions.moneys"
                            class="form-control pre-remove-btn"
                            :id="'expense_'+ind"
                            v-model.number="expense.Amount"
                            v-on:blur="countExp(), triggerHeightCheck()"
                            :aria-label="'expense amount '+ind"
                            type="text"
                            tabindex="0">
                          </cleave>
                          <button v-if="ind != 0" class="remove-item-btn" v-on:click="removeExpense(ind)" tabindex="0" type="button" role="button"><i class="icon-remove" aria-label="Remove expense"></i></button>
                          <br/><span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                        </ValidationProvider>
                      </div>
                    </div>

                    <button class="add-button p-2" v-on:click="addExpense" tabindex="0" type="button" role="button">+ Add another cost</button>
                    <hr>
                    <span class="main-color">Added expenses</span>
                    <h4>${{formatCurrency(totalExpenseAmount, true)}} <small>per month</small></h4>
                  </div>
                </div>
              </div>

              <!-- <div class="row" v-if="data.stepData.familyType == Enum.familyType.SINGLE">
                <div class="col-auto">
                  <div class="info-box">
                    <div class="form-row">
                      <div class="col-1 icon"><i class="icon-info"></i></div>
                      <div class="col text">
                        <span v-html="pageContent.pages.expenses.livingExpensesSingleInfoText"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div> -->

              <div v-if="data.stepData.familyType == Enum.familyType.SINGLE_DEPENDANT || data.stepData.familyType == Enum.familyType.COUPLE_DEPENDANT" class="expense-dependant-section">
                <div class="row no-gutters mt-5">
                  <div class="col-auto">
                    <h3 tabindex="0">
                      Annual education costs
                    </h3>
                  </div>
                  <div class="col-auto">
                    <b-button id="tooltip-education" variant="tooltip" aria-label="tooltip education cost"><i class="icon-help"></i></b-button>
                    <b-tooltip target="tooltip-education" placement="top">
                      <span v-html="pageContent.pages.expenses.dependantsTooltipText"></span>
                    </b-tooltip>
                  </div>
                </div>

                <!-- looop thru dependants - first check family type -->
                <div class="row" v-for="(dependant, ind) in dependants" :key="ind">
                  <div class="col">
                    <div class="form-group">
                      <ValidationProvider mode="lazy" :name="'dependant_'+ind" :rules="'required:'+pageContent.pages.expenses.dependantAmountRangeErrorMessage+'|between:'+config.fields.dependantAmount.min+','+config.fields.dependantAmount.max+','+pageContent.pages.expenses.dependantAmountRangeErrorMessage" v-slot="{ errors }">
                        <label :for="'dependant_'+ind" tabindex="0">{{dependant.Age}} year old dependant</label><br/>
                        <cleave
                          data-analytics-capture-value="true"
                          :options="formatOptions.moneys"
                          class="form-control"
                          :id="'dependant_'+ind"
                          v-model.number="dependant.Amount"
                          v-on:blur="triggerHeightCheck"
                          type="text"
                          tabindex="0">
                        </cleave>
                        <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                      </ValidationProvider>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row no-gutters mt-5">
                <div class="col-auto">
                  <h3>
                    <label for="disabilityCost" tabindex="0">Cost of disability</label>
                  </h3>
                </div>
                <div class="col-auto">
                  <b-button id="tooltip-disability" variant="tooltip" aria-label="tooltip disability cost"><i class="icon-help"></i></b-button>
                  <b-tooltip target="tooltip-disability" placement="top">
                    <span v-html="pageContent.pages.expenses.disabilityTooltipText"></span>
                  </b-tooltip>
                </div>
              </div>

              <label tabindex="0" v-if="pageContent.pages.expenses.disabilityText">{{pageContent.pages.expenses.disabilityText}}</label>

              <div class="form-group">
                <ValidationProvider mode="lazy" name="disabilityCost" :rules="'required:'+pageContent.pages.expenses.disabilityCostError+'|between:'+config.fields.disabilityCost.min+','+config.fields.disabilityCost.max+','+pageContent.pages.expenses.disabilityCostError" v-slot="{ errors }">
                  <cleave
                    data-analytics-capture-value="true"
                    :options="formatOptions.moneys"
                    class="form-control"
                    id="disabilityCost"
                    name="disabilityCost"
                    v-model.number="data.stepData.disabilityCost"
                    :value="data.stepData.disabilityCost"
                    v-on:blur="triggerHeightCheck"
                    type="text"
                    tabindex="0">
                  </cleave>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                </ValidationProvider>
              </div>

              <div class="row no-gutters mt-4">
                <div class="col-auto">
                  <h3>
                    <label for="funeralExpenses" tabindex="0">Funeral expenses</label>
                  </h3>
                </div>
                <div class="col-auto">
                  <b-button id="tooltip-funeral" variant="tooltip" aria-label="tooltip funeral expenses"><i class="icon-help"></i></b-button>
                  <b-tooltip target="tooltip-funeral" placement="top">
                    The default value of $10,000 is based on ASIC's MoneySmart website.
                  </b-tooltip>
                </div>
              </div>
              <label tabindex="0" v-if="pageContent.pages.expenses.deathCoverExpenseText">{{pageContent.pages.expenses.deathCoverExpenseText}}</label>
              <div class="form-group">
                <ValidationProvider mode="lazy" name="funeralExpenses" :rules="'required:'+pageContent.pages.expenses.funeralExpensesError+'|between:'+config.fields.funeralExpenses.min+','+config.fields.funeralExpenses.max+','+pageContent.pages.expenses.funeralExpensesError" v-slot="{ errors }">
                  <cleave
                    data-analytics-capture-value="true"
                    :options="formatOptions.moneys"
                    class="form-control"
                    id="funeralExpenses"
                    name="funeralExpenses"
                    v-model.number="data.stepData.funeralExpenses"
                    :value="data.stepData.funeralExpenses"
                    v-on:blur="triggerHeightCheck"
                    type="text"
                    tabindex="0">
                  </cleave>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                </ValidationProvider>
              </div>

              <div class="nav-button row">
                <router-link class="assumption-button col" :to="{ name: 'Assumptions', params: {activeTab: 'assumptions' } }" tabindex="0" type="button" role="button"><i class="icon-edit"></i> Edit assumptions </router-link>
                <router-link class="back-button col" to="/income" tabindex="0" type="button" role="button">Back</router-link>
                <!-- <router-link class="next-button col" to="/results" tabindex="0" role="button">Next</router-link> -->
                <button class="next-button col" v-bind:class="[valid ? 'is-active' : 'is-disabled']"  type="submit" tabindex="0" role="button">Next</button>
              </div>
            </form>
          </ValidationObserver>

        </div>
      </div>

    </div>
    <CustomFooter></CustomFooter>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapFields } from "vuex-map-fields";
import Cleave from "vue-cleave-component";

import { ValidationProvider, extend, ValidationObserver } from 'vee-validate';
import { required, between } from 'vee-validate/dist/rules';
import CustomFooter from '../components/CustomFooter';
import _ from 'lodash';

extend('required', {
  ...required,
  params: ['msg'],
  message: '{msg}'
});

extend('between', {
  ...between,
  params: ['min', 'max', 'msg'],
  validate(value, { min, max }) {
    return value >= min && value <= max;
  },
  message: '{msg}'
});

export default {
  name: 'Expenses',
  components: { Cleave, ValidationProvider, ValidationObserver, CustomFooter },
  computed: {
    ...mapFields(["detailedExpenses", "expenses", "data"]),
    ...mapGetters(["dependants", "Enum", "config", "formatOptions", "pageContent"]),
  },
  methods: {
    clickDetailedExpenses(e) {
      this.triggerHeightCheck();
      this.$store.commit("updateDetailedExpenses",e.target.checked);
    },
    addExpense() {
      this.expenses.push({"Name":'', "Amount":0, "Error": false});
      this.countExp();
      this.triggerHeightCheck();
    },
    removeExpense(ind) {
      if(this.expenses.length > 1) {
        this.expenses.splice(ind, 1);
      }
      else {
        this.expenses.pop();
        this.expenses.push({"Name":'', "Amount":0, "Error": false});
      }
      this.countExp();
      this.triggerHeightCheck();
    },
    countExp() {
      this.totalExpenseAmount = 0;
      this.expenses.forEach(exp => {
        this.totalExpenseAmount += exp.Amount;
      });
    },
    checkDependants(min, max, ind) {
      var val = parseInt(this.dependants[ind].Amount);
			if(isNaN(val) || val < min) {
        this.dependants[ind].Amount = min;
				this.dependants[ind].Error = true;
			}
			else if(val > max) {
        this.dependants[ind].Amount = max;
				this.dependants[ind].Error = true;
			}
			else {
				this.dependants[ind].Error = false;
			}
		},
    formatCurrency: (value, noCents = false) => {
      return value === null
        ? ""
        : noCents
        ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : parseFloat(value)
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    triggerHeightCheck: function() {
      // Add slight delay otherwise incorrect height is read
      var rf = this;
      setTimeout(function() {
        rf.$refreshIframeHeight();
      }, 300);
    },
    onSubmit () {
      this.$refs.form.validateWithInfo().then(result => {
        if (!result.isValid) {
          var fieldArr = _.valuesIn(result.fields);
          var firstError = null;
          fieldArr.forEach(field => {
            if(field.invalid) {
              firstError = document.getElementById(field.id);
              return;
            }
          })
          if(firstError) {
            firstError.scrollIntoView({behavior: 'smooth'});
          }
          this.triggerHeightCheck();
          return;
        }
        this.$router.push('/results');
      });
    },
  },
  mounted() {
    this.countExp();
    this.triggerHeightCheck();
    this.$scrollIframeTop();
    window.addEventListener("resize", this.$refreshIframeHeight);
  },
  destroyed() {
    window.removeEventListener("resize", this.$refreshIframeHeight);
  },
  beforeDestroy() {
    // send analytics - complete form step
    var dataObj = {
      "livingExpensesPerMonth": this.data.stepData.expensesAmountPerMonth,
      "disabilityCost": this.data.stepData.disabilityCost,
      "funeralExpenses": this.data.stepData.funeralExpenses,
      "dependants": [],
      "expenses": [],
    };
    if(this.detailedExpenses) {
      var total = 0;
      this.expenses.forEach(exp => {
        total += exp.Amount;
        var newExp = {"Name": exp.Name, "Amount": exp.Amount}
        dataObj.expenses.push(newExp);
      })
      dataObj.livingExpensesPerMonth = total;
    }
    if(this.data.stepData.familyType == this.Enum.familyType.SINGLE_DEPENDANT || this.data.stepData.familyType == this.Enum.familyType.COUPLE_DEPENDANT) {
      this.dependants.forEach(dep => {
        var newDep = {"Age": dep.Age, "Amount": dep.Amount};
        dataObj.dependants.push(newDep);
        this.$gtag.event("step-about", {
          "event_category": "field-data",
          "event_label": "dependant education cost",
          "value": dep.Amount
        })
      });
    }
    // console.log(JSON.stringify(dataObj));
    this.$gtag.event("step-expenses", {
      "event_category": "field-data",
      "event_label": "living expenses per month",
      "value": dataObj.livingExpensesPerMonth
    })
    this.$gtag.event("step-expenses", {
      "event_category": "field-data",
      "event_label": "disability cost",
      "value": this.data.stepData.disabilityCost
    })
    this.$gtag.event("step-expenses", {
      "event_category": "field-data",
      "event_label": "funeral expenses",
      "value": dataObj.livingExpensesPerMonth
    })
    this.$gtag.event("step-expenses", {
      "event_category": "field-data",
      "event_label": "living expenses per month",
      "value": this.data.stepData.funeralExpenses
    })

    this.$gtag.event("step-expenses", {
      "event_category": "form-step-progress",
      "event_label": JSON.stringify(dataObj),
    })

		// clear any asset/dependent errors when moving step. should not be any errors.
    // Was showing error if you went to next step and back to income step.
    this.expenses.forEach(exp => {
      exp.Error = false;
    });
		this.dependants.forEach(dep => {
      dep.Error = false;
    });
  },
  data() {
    return {
      totalExpenseAmount: 0,
    };
  }
}


</script>
